import {useCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import type {MarkdownComposerRef} from '@github-ui/commenting/useMarkdownBody'
import {VALUES as COMMENTING_VALUES} from '@github-ui/commenting/Values'
import {CopilotSurvey} from '@github-ui/copilot-survey-card'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IssueAgentTasksProvider} from '@github-ui/issue-agent-tasks/contexts/IssueAgentTasksContext'
import {IssueBody} from '@github-ui/issue-body/IssueBody'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useBeforeUnload} from '@github-ui/react-router'
import {handleRepositoryDataError, reloadPage} from '@github-ui/relay-error-helpers'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {useCacheQueryLoader} from '@github-ui/relay-query-cache/hooks/use-cache-query-loader'
import {useScribeArtifacts} from '@github-ui/scribe-artifacts'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {useSubIssueState} from '@github-ui/sub-issues/SubIssueStateContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
import {ISSUE_EVENTS} from '@github-ui/timeline-items/Events'
import {getHighlightedEventText} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {Heading} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {CLASS_NAMES} from '../constants/class-names'
import {IDS} from '../constants/ids'
import {TEST_IDS} from '../constants/test-ids'
import {VALUES} from '../constants/values'
import {useInputElementActiveContext} from '../contexts/InputElementActiveContext'
import {useHash} from '../hooks/use-hash'
import {useSidePanelScrollReset} from '../hooks/use-side-panel-scroll-reset'
import type {ItemIdentifier} from '../types/issue'
import type {IssueViewerIssue$key} from './__generated__/IssueViewerIssue.graphql'
import type {IssueViewerViewer$key} from './__generated__/IssueViewerViewer.graphql'
import type {IssueViewerViewQuery, IssueViewerViewQuery$variables} from './__generated__/IssueViewerViewQuery.graphql'
import {ArtifactsSection} from './artifacts-section/ArtifactsSection'
import {CreateArtifactMenuButton} from './artifacts-section/CreateArtifactMenuButton'
import {
  shouldFetchIssueArtifacts,
  shouldShowArtifactsSection,
  shouldShowCreateArtifactButton,
} from './artifacts-section/issue-artifacts-visibility'
import {ContentWrapper} from './ContentWrapper'
import {ConvertedToDiscussionBanner} from './ConvertedToDiscussionBanner'
import {EmuContributionBlockedBanner} from './EmuContributionBlockedBanner'
import {STICKY_HEADER_SIDEBAR_OFFSET} from './header/constants/sticky-elements'
import {Header} from './header/Header'
import {StickyHeader} from './header/StickyHeader'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import {IssueSidebar} from './IssueSidebar'
import {IssueTimelineErrorFallback} from './IssueTimelineErrorFallback'
import {IssueTimelineLoading} from './IssueTimelineLoading'
import styles from './IssueViewer.module.css'
import {IssueViewerLoading} from './IssueViewerLoading'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import {OptimisticUpdateRetry} from './OptimisticUpdateRetry'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from './OptionConfig'
import {RecentAgentActivity} from './recent-agent-activity/RecentAgentActivity'
import {NotFoundError, renderIssueViewerErrors} from './shared/IssueViewerError'
import {SidePanelMetadataDrawer} from './SidePanelMetadataDrawer'
import {SignedOutBanner} from './SignedOutBanner'
import {IssueTimeline} from './timeline/IssueTimeline'
import {buildTimelineConnectionIds, TimelineConnectionProvider} from './timeline/TimelineConnectionContext'

export type IssueViewerQueries = {
  issueViewerViewQuery: IssueViewerViewQuery
}

type IssueViewerProps = {
  itemIdentifier: ItemIdentifier
  optionConfig?: OptionConfig
}

type IssueViewerSecondaryDataProps = {
  secondaryIssueData?: IssueSecondaryNode
  timelinePage?: number
}

type IssueViewerInternalProps = {
  optionConfig: OptionConfig
  issueViewerViewRef: PreloadedQuery<IssueViewerViewQuery>
  owner: string
  repo: string
  number: number
  retryQuery?: () => void
} & IssueViewerSecondaryDataProps

type IssueViewerInternalFragmentProps = {
  optionConfig: OptionConfig
  viewerFragment: IssueViewerViewer$key | null
  issueFragment: IssueViewerIssue$key
  isRepoOwnerEnterpriseManaged?: boolean | null
} & IssueViewerSecondaryDataProps

type IssueViewerWithSecondaryProps = IssueViewerInternalProps & {
  owner: string
  repo: string
  number: number
}

function doVariablesMatch(
  queryVariables: IssueViewerViewQuery$variables,
  expectedVariables: IssueViewerViewQuery$variables,
): boolean {
  return (
    queryVariables.number === expectedVariables.number &&
    queryVariables.owner === expectedVariables.owner &&
    queryVariables.repo === expectedVariables.repo &&
    queryVariables.allowedOwner === expectedVariables.allowedOwner &&
    queryVariables.hideTimeline === expectedVariables.hideTimeline &&
    queryVariables.count === expectedVariables.count &&
    queryVariables.skip === expectedVariables.skip
  )
}

const IssueCommentComposer = React.lazy(async () => {
  const m = await import('@github-ui/commenting/IssueCommentComposer')
  return {default: m.IssueCommentComposer}
})

const SubIssuesList = React.lazy(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesList')
  return {default: m.SubIssuesList}
})

const SubIssuesCreateDialog = React.lazy(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesCreateDialog')
  return {default: m.SubIssuesCreateDialog}
})

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerViewGraphqlQuery = graphql`
  query IssueViewerViewQuery(
    $repo: String!
    $owner: String!
    $number: Int!
    $allowedOwner: String
    $hideTimeline: Boolean = false
    $skip: Int
    $count: Int
  ) {
    repository(name: $repo, owner: $owner) @catch(to: RESULT) {
      id @required(action: THROW)
      isOwnerEnterpriseManaged
      owner {
        userNameDisplayConfiguration
      }
      issue(number: $number) @catch(to: RESULT) {
        id @required(action: THROW)
        ...IssueViewerIssue
          @arguments(allowedOwner: $allowedOwner, hideTimeline: $hideTimeline, skip: $skip, count: $count)
      }
    }
    safeViewer {
      ...IssueViewerViewer
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const issueViewerViewFragment = graphql`
  fragment IssueViewerIssue on Issue
  @argumentDefinitions(
    allowedOwner: {type: "String", defaultValue: null}
    hideTimeline: {type: "Boolean", defaultValue: false}
    skip: {type: "Int"}
    count: {type: "Int", defaultValue: 15}
  ) {
    id
    number
    viewerCanUpdateMetadata
    repository {
      name
      isArchived
      owner {
        login
      }
    }
    updatedAt
    ...Header
    ...IssueBody
    ...IssueCommentComposer
    ...IssueSidebarPrimaryQuery @arguments(allowedOwner: $allowedOwner)
    ...IssueTimelineIssueFragment @skip(if: $hideTimeline) @arguments(skip: $skip, count: $count)
    # sub-issues
    subIssuesSummary {
      total
      completed
    }
    # eslint-disable-next-line relay/must-colocate-fragment-spreads
    ...useRecentAgentActivity_issue
  }
`
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const issueViewerViewerFragment = graphql`
  fragment IssueViewerViewer on User {
    # Is needed to be passed to the IssueSidebar and the SubscriptionSection component in order not to show the notification customization option for EMU
    isEnterpriseManagedUser
    enterpriseManagedEnterpriseId
    login
    ...IssueCommentComposerViewer
    # Read as plain props by EditIssueAssigneesSection (self-assign).
    __typename
    id
    name
    profileResourcePath
    avatarUrl(size: 64)
  }
`

export function IssueViewer({itemIdentifier, optionConfig = ISSUE_VIEWER_DEFAULT_CONFIG}: IssueViewerProps) {
  const {repo, owner, number} = itemIdentifier
  const urlQuery = ssrSafeLocation.search
  const searchParams = new URLSearchParams(urlQuery)
  const timelinePage = searchParams.get('timeline_page')

  const [issueViewerViewRef, loadIssueViewerView] = useCacheQueryLoader<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    DEFAULT_RELAY_CACHE_TTL,
    ['allowedOwner'],
  )
  const issueViewerContainerRef = useRef<HTMLDivElement>(null)

  const fetchPolicy = optionConfig.issueQueriesFetchingPolicy?.fetchPolicy

  const expectedVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      allowedOwner: optionConfig.allowedProjectOwner ?? null,
      hideTimeline: optionConfig.hideTimeline,
      count: VALUES.timelineBackPageSize,
      skip: null,
    }),
    [owner, repo, number, optionConfig.allowedProjectOwner, optionConfig.hideTimeline],
  )

  useEffect(() => {
    const variablesMatch = issueViewerViewRef && doVariablesMatch(issueViewerViewRef.variables, expectedVariables)

    if (!optionConfig.preloadedQueries?.issueViewerViewQuery && (!issueViewerViewRef || !variablesMatch)) {
      loadIssueViewerView(expectedVariables, {
        fetchPolicy,
      })
    }
  }, [
    loadIssueViewerView,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    fetchPolicy,
    issueViewerViewRef,
    expectedVariables,
  ])

  // Reset scroll position to top when a new issue loads inside the side panel.
  // Firefox preserves the scroll position of the overflow container across re-renders,
  // causing the new issue to appear scrolled partway down.
  useSidePanelScrollReset(optionConfig.insideSidePanel, owner, repo, number)

  const retryQuery = useCallback(
    () => loadIssueViewerView(expectedVariables, {fetchPolicy: 'network-only'}),
    [loadIssueViewerView, expectedVariables],
  )

  // Workaround for Chrome bug with container-type: inline-size causing elements to render with 0 dimensions
  // during SPA navigation. See: https://issues.chromium.org/issues/379030050
  // Only triggers when the bug is detected (header has 0 height).
  useEffect(() => {
    if (!isFeatureEnabled('issues_react_chrome_container_query_fix')) return

    const container = issueViewerContainerRef.current
    if (!container) return

    // Check after a short delay to allow initial render to complete
    const timeoutId = setTimeout(() => {
      const header = container.querySelector('[data-testid="issue-header"]')
      // Only apply fix if header exists but has 0 height (bug detected)
      if (header && header.getBoundingClientRect().height === 0) {
        // Force layout recalculation by toggling display
        const originalDisplay = container.style.display
        container.style.display = 'none'
        void container.offsetHeight
        container.style.display = originalDisplay
      }
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [owner, repo, number])

  if (!issueViewerViewRef) return <IssueViewerLoading optionConfig={optionConfig} />

  // Check if the query ref variables match the requested issue - needed for fast viewer issue navigation
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  return (
    <PreloadedQueryBoundary
      key={`${owner}-${repo}-${number}`}
      onRetry={() =>
        loadIssueViewerView(
          {
            owner,
            repo,
            number,
            allowedOwner: optionConfig.allowedProjectOwner ?? null,
            hideTimeline: optionConfig.hideTimeline,
            count: VALUES.timelineBackPageSize,
            skip: null,
          },
          {fetchPolicy: 'network-only'},
        )
      }
      fallback={(retry, error) =>
        renderIssueViewerErrors(retry, error, optionConfig.insideSidePanel ? optionConfig.onClose : undefined)
      }
      critical
    >
      <div
        className={clsx(
          styles.issueViewerContainer,
          styles.mainContainer,
          isFeatureEnabled('issues_sticky_header') && styles.mainContainer_stickyHeader,
        )}
        ref={issueViewerContainerRef}
      >
        <Suspense fallback={<IssueViewerLoading optionConfig={optionConfig} />}>
          <IsssueViewerWithSecondary
            owner={owner}
            repo={repo}
            number={number}
            issueViewerViewRef={issueViewerViewRef}
            optionConfig={optionConfig}
            retryQuery={retryQuery}
            timelinePage={(timelinePage && parseInt(timelinePage, 10)) || undefined}
          />
        </Suspense>
      </div>
    </PreloadedQueryBoundary>
  )
}

function IssueViewerInternal({
  owner,
  repo,
  number,
  issueViewerViewRef,
  optionConfig,
  secondaryIssueData,
  timelinePage,
  retryQuery,
}: IssueViewerInternalProps) {
  const {repository, safeViewer} = usePreloadedQuery<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    issueViewerViewRef,
  )

  // ensure query ref variables still match after Suspense resolves
  // This catches race conditions during rapid navigation where data may become stale
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  const repositoryResult = handleRepositoryDataError(repository, {
    shouldReloadOnError: !optionConfig.insideSidePanel,
    subIssueFromOptimisticUpdate: optionConfig.subIssueFromOptimisticUpdate,
  })
  if (!repositoryResult || !repositoryResult.value) {
    if (optionConfig.subIssueFromOptimisticUpdate && retryQuery) {
      return <OptimisticUpdateRetry retryQuery={retryQuery} optionConfig={optionConfig} />
    }
    if (optionConfig.insideSidePanel) {
      return <NotFoundError onClose={optionConfig.onClose} />
    }
    return null
  }

  if (repositoryResult.value.issue?.ok === false) {
    const errors = repositoryResult.value.issue.errors
    const hasNotFoundError = errors?.some(e => e && typeof e === 'object' && 'type' in e && e.type === 'NOT_FOUND')

    if (hasNotFoundError) {
      if (optionConfig.subIssueFromOptimisticUpdate && retryQuery) {
        return <OptimisticUpdateRetry retryQuery={retryQuery} optionConfig={optionConfig} />
      }
      if (!optionConfig.insideSidePanel) {
        // if the issue is not found, reload the page to get a fresh 404 or deleted issue page from the server
        // only if we are viewing the issue in the issue show page (not in a side panel)
        reloadPage()
        return null
      }
      return <NotFoundError onClose={optionConfig.onClose} />
    }
    throw new Error(`IssueViewer: repository.issue is errored. Errors: ${JSON.stringify(errors)}`)
  }

  if (!repositoryResult.value.issue?.value) {
    return null
  }

  return (
    <IssueAgentTasksProvider owner={owner} repo={repo} issueNumber={number}>
      <UserNameDisplayContextProvider value={repositoryResult.value.owner.userNameDisplayConfiguration}>
        <IssueViewerInternalFragment
          viewerFragment={safeViewer || null}
          issueFragment={repositoryResult.value.issue.value}
          optionConfig={optionConfig}
          isRepoOwnerEnterpriseManaged={repositoryResult.value.isOwnerEnterpriseManaged}
          secondaryIssueData={secondaryIssueData}
          timelinePage={timelinePage}
        />
      </UserNameDisplayContextProvider>
    </IssueAgentTasksProvider>
  )
}

export function getPermalinkType(hash: string): 'issuecomment' | 'event' | 'cross_reference' | null {
  const hashWithoutSymbol = hash.startsWith('#') ? hash.substring(1) : hash
  if (!hashWithoutSymbol) return null
  if (hashWithoutSymbol.startsWith('issuecomment-')) return 'issuecomment'
  if (hashWithoutSymbol.startsWith('event-')) return 'event'
  if (hashWithoutSymbol.startsWith('ref-issue-') || hashWithoutSymbol.startsWith('ref-pullrequest-'))
    return 'cross_reference'
  return null
}

export function IssueViewerInternalFragment({
  viewerFragment,
  issueFragment,
  optionConfig,
  isRepoOwnerEnterpriseManaged,
  secondaryIssueData,
  timelinePage,
}: IssueViewerInternalFragmentProps) {
  const issue = useFragment(issueViewerViewFragment, issueFragment)
  const viewer = useFragment(issueViewerViewerFragment, viewerFragment) || null

  // Fire onLoad callback when issue data is available (component has mounted after Suspense resolved)
  const onLoad = optionConfig.onLoad
  useEffect(() => {
    onLoad?.()
  }, [onLoad])

  // Record telemetry for permalink type on page load
  useEffect(() => {
    const permalinkType = getPermalinkType(ssrSafeLocation.hash)
    if (permalinkType) {
      sendEvent('issue_viewer.permalink.viewed', {permalink_type: permalinkType})
    }
  }, [])

  const hasSubIssues = useHasSubIssues(issue)
  const canEditSubIssues = useCanEditSubIssues(issue)
  const {hideTimeline = false} = optionConfig

  const showEmuContributionBlockedBanner =
    viewer && !!viewer.enterpriseManagedEnterpriseId && !isRepoOwnerEnterpriseManaged
  const showCommentComposer = viewer && !showEmuContributionBlockedBanner && !optionConfig.isReadonly
  const issueArtifactsResourceId = secondaryIssueData?.databaseId ?? 0
  const {data: issueArtifacts} = useScribeArtifacts({
    resourceType: 'issue',
    resourceId: issueArtifactsResourceId,
    enabled: shouldFetchIssueArtifacts(issueArtifactsResourceId),
  })
  const showCreateArtifactButton = shouldShowCreateArtifactButton(issueArtifacts)
  const showArtifactsSection = shouldShowArtifactsSection(issueArtifacts)
  const [focusArtifactsSectionOnShow, setFocusArtifactsSectionOnShow] = useState(false)
  const [focusCreateArtifactButtonOnShow, setFocusCreateArtifactButtonOnShow] = useState(false)
  const handleInitialArtifactCreated = useCallback(() => {
    setFocusArtifactsSectionOnShow(true)
  }, [])
  const handleArtifactsSectionFocusTargetReady = useCallback(() => {
    setFocusArtifactsSectionOnShow(false)
  }, [])
  const handleLastArtifactDeleted = useCallback(() => {
    setFocusCreateArtifactButtonOnShow(true)
  }, [])
  const handleCreateArtifactButtonFocusTargetReady = useCallback(() => {
    setFocusCreateArtifactButtonOnShow(false)
  }, [])

  const effectiveOptionConfig = useMemo(
    () => (showEmuContributionBlockedBanner ? {...optionConfig, showIssueCreateButton: false} : optionConfig),
    [optionConfig, showEmuContributionBlockedBanner],
  )

  const composerRef = useRef<MarkdownComposerRef>(null)
  const issueBodyRef = useRef<HTMLDivElement>(null)
  const {startCommentEdit, cancelCommentEdit, isCommentEditActive} = useCommentEditsContext()
  const issueBodyKey = `issue-${issue.id}-body`
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()

  // Reset comment and input states on issue change
  useEffect(() => {
    clearInputElementStates()
  }, [issue.id, clearInputElementStates])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isCommentEditActive())
  }, [isCommentEditActive, onIssueEditStateChange])

  const onCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    requestAnimationFrame(() => composerRef.current?.focus())
  }, [])

  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditStart?.(id)
    },
    [issue.id, optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, issue.id, optionConfig],
  )

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isCommentEditActive()) {
        event.preventDefault()
        return (event.returnValue = '')
      }
    },
    [isCommentEditActive],
  )

  useBeforeUnload(beforeUnload)

  const metadataContent = useMemo(
    () => (
      <IssueSidebar
        sidebarKey={issue}
        sidebarSecondaryKey={secondaryIssueData}
        lazySecondaryData={secondaryIssueData}
        viewer={viewer}
        optionConfig={optionConfig}
      />
    ),
    [issue, optionConfig, secondaryIssueData, viewer],
  )

  const metadataPane = useMemo(
    () => (
      <div className={styles.issueViewerMetadataPane} data-testid={TEST_IDS.issueViewerMetadataPane}>
        {isFeatureEnabled('issues_sticky_header') ? (
          <Heading as="h2" className="sr-only">
            {LABELS.metadataHeader}
          </Heading>
        ) : (
          <>
            <Heading as="h2" className={styles.metadataHeader}>
              {LABELS.metadataHeader}
            </Heading>
            <Heading as="h2" className={`${styles.largeScreenMetadataHeader} sr-only`}>
              {LABELS.metadataHeader}
            </Heading>
          </>
        )}
        {metadataContent}
      </div>
    ),
    [metadataContent],
  )

  const handleBodyEditStateChange = useCallback(
    (isEditing: boolean) => {
      optionConfig?.onIssueEditStateChange?.(isEditing)
      if (isEditing) {
        startCommentEdit(issueBodyKey)
      } else {
        cancelCommentEdit(issueBodyKey)
      }
    },
    [cancelCommentEdit, issueBodyKey, optionConfig, startCommentEdit],
  )
  // This is used to subscribe to hash-change events
  // Specifically, it is used to detect when the user has (soft) navigated to a comment in the same issue
  useHash()

  const {createDialogOpen, activeIssueId, closeCreateDialog} = useSubIssueState()

  const timelineConnectionIds = useMemo(() => {
    return buildTimelineConnectionIds(issue.id)
  }, [issue.id])

  const {isSticky: isHeaderSticky, sentinelRef} = useStickyObserver()
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const sidePanelReturnFocusRef = useRef<HTMLButtonElement>(null)

  // Expose the sticky header's measured height (plus any notification-shelf offset) as a CSS
  // variable on the viewer grid so descendant sticky elements (e.g. the sub-issues list header)
  // can stick flush against the header instead of relying on a hardcoded height.
  const viewerGridRef = useRef<HTMLDivElement>(null)
  const [stickyHeaderNode, setStickyHeaderNode] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!stickyHeaderNode || typeof ResizeObserver === 'undefined') return
    let raf = 0
    let last = -1
    const observer = new ResizeObserver(([entry]) => {
      const height = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect.height ?? 0
      if (height === last) return
      last = height
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        viewerGridRef.current?.style.setProperty(
          '--issue-sticky-header-offset',
          `calc(var(--base-sticky-header-height, 0px) + ${height}px)`,
        )
      })
    })
    observer.observe(stickyHeaderNode)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [stickyHeaderNode])

  const issuePath = `/${issue.repository.owner.login}/${issue.repository.name}/issues/${issue.number}`
  const issueUrl = ssrSafeLocation.origin ? `${ssrSafeLocation.origin}${issuePath}` : issuePath

  const issueContentArea = (
    <div
      className={clsx(
        styles.contentArea,
        optionConfig.useViewportQueries ? styles.contentArea_viewport : styles.contentArea_container,
      )}
    >
      <RecentAgentActivity issue={issue} issueBodyRef={issueBodyRef} />
      <div ref={issueBodyRef} data-testid={TEST_IDS.issueViewerIssueContainer} tabIndex={-1}>
        <IssueBody
          issue={issue}
          issueBodySecondary={secondaryIssueData}
          secondaryKey={secondaryIssueData}
          onLinkClick={optionConfig.onLinkClick}
          onIssueEditStateChange={handleBodyEditStateChange}
          onIssueUpdate={optionConfig.onIssueUpdate}
          onCommentReply={onCommentReply}
          isIssueEditActive={isCommentEditActive}
          highlightedEventText={ssrSafeLocation.hash}
          insideSidePanel={optionConfig.insideSidePanel}
          isReadonly={optionConfig.isReadonly || false}
          issueBodyTaskListAction={
            showCreateArtifactButton ? (
              <CreateArtifactMenuButton
                resourceId={issueArtifactsResourceId}
                focusOnMount={focusCreateArtifactButtonOnShow}
                onCreated={handleInitialArtifactCreated}
                onFocusTargetReady={handleCreateArtifactButtonFocusTargetReady}
              />
            ) : undefined
          }
        />
      </div>
      {showArtifactsSection && (
        <ArtifactsSection
          resourceId={issueArtifactsResourceId}
          artifacts={issueArtifacts ?? []}
          isLastSection={!hasSubIssues}
          focusOnMount={focusArtifactsSectionOnShow}
          onFocusTargetReady={handleArtifactsSectionFocusTargetReady}
          onLastArtifactDeleted={handleLastArtifactDeleted}
        />
      )}
      {hasSubIssues && (
        <div
          data-testid={TEST_IDS.subIssuesIssueContainer}
          className={clsx(
            styles.subIssuesContainer,
            !isFeatureEnabled('issue_inline_avatars') && styles.subIssuesContainer_container,
          )}
        >
          <Suspense fallback={null}>
            <SubIssuesList
              issue={secondaryIssueData}
              onSubIssueClick={optionConfig.onSubIssueClick}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={!canEditSubIssues || optionConfig.isReadonly}
              queryToPreheat={IssueViewerViewGraphqlQuery}
            />
          </Suspense>
        </div>
      )}
      <ErrorBoundary fallback={<IssueTimelineErrorFallback />} critical>
        <Suspense fallback={<IssueTimelineLoading delayedShow />}>
          <div data-testid={TEST_IDS.issueViewerCommentsContainer} className={CLASS_NAMES.commentsContainer}>
            <div className={styles.commentsContainer}>
              {!hideTimeline && (
                <IssueTimeline
                  issue={issue}
                  issueSecondary={secondaryIssueData}
                  viewer={viewer?.login ?? null}
                  highlightedEvent={getHighlightedEventText(ssrSafeLocation.hash, ISSUE_EVENTS)}
                  onCommentReply={onCommentReply}
                  onCommentChange={onCommentChange}
                  onCommentEditCancel={onCommentEditCancel}
                  optionConfig={optionConfig}
                  page={timelinePage}
                />
              )}
              {!viewer && <SignedOutBanner />}
            </div>
          </div>
          {showCommentComposer && (
            <Suspense fallback={null}>
              <IssueCommentComposer
                ref={composerRef}
                issue={issue}
                issueSecondary={secondaryIssueData}
                viewer={viewer}
                onChange={() => {
                  startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                  optionConfig.onCommentEditStart?.(IDS.newComment)
                }}
                onSave={() => {
                  cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                  optionConfig.onCommentEditCancel?.(IDS.newComment)
                }}
                onCancel={() => {
                  cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                  optionConfig.onCommentEditCancel?.(IDS.newComment)
                }}
                onNewIssueComment={optionConfig.onNewIssueComment}
                onStateChange={optionConfig.onIssueUpdate}
                fileUploadsEnabled
                insideSidePanel={optionConfig.insideSidePanel ?? false}
                relayConnectionId={timelineConnectionIds.back}
              />
            </Suspense>
          )}
        </Suspense>
        {showEmuContributionBlockedBanner && <EmuContributionBlockedBanner />}
      </ErrorBoundary>
    </div>
  )

  return (
    <TimelineConnectionProvider value={timelineConnectionIds}>
      {optionConfig.copilotSurveyBanner && <CopilotSurvey {...optionConfig.copilotSurveyBanner} />}
      {secondaryIssueData?.discussion && (
        <ConvertedToDiscussionBanner discussionUrl={secondaryIssueData?.discussion?.url ?? ''} />
      )}
      {stickyHeaderEnabled ? (
        <>
          <StickyHeader
            issueKey={secondaryIssueData}
            linkedSecurityAlertsData={secondaryIssueData}
            blockedBySecondaryData={secondaryIssueData}
            trackedBySecondaryData={secondaryIssueData}
            subIssueSecondaryData={secondaryIssueData}
            issueNumber={issue.number}
            ownerLogin={issue.repository.owner.login}
            repoName={issue.repository.name}
            issueUrl={issueUrl}
            optionConfig={effectiveOptionConfig}
            isHeaderSticky={isHeaderSticky}
            onInfoClick={() => setSidePanelOpen(true)}
            infoButtonRef={sidePanelReturnFocusRef}
            rootRef={setStickyHeaderNode}
          />
          <div
            ref={viewerGridRef}
            className={clsx(
              styles.viewerGrid,
              optionConfig.useViewportQueries ? styles.viewerGrid_viewport : styles.viewerGrid_container,
            )}
          >
            <div className={styles.viewerTitle}>
              <Header
                issue={issue}
                headerSecondary={secondaryIssueData}
                parent={secondaryIssueData?.parent}
                linkedSecurityAlertsData={secondaryIssueData}
                blockedBySecondaryData={secondaryIssueData}
                trackedBySecondaryData={secondaryIssueData}
                subIssueSecondaryData={secondaryIssueData}
                optionConfig={effectiveOptionConfig}
              />
              <div ref={sentinelRef} className={styles.stickyHeaderSentinel} aria-hidden="true" />
            </div>
            <div className={styles.viewerContent}>{issueContentArea}</div>
            <div
              className={clsx(
                styles.viewerSidePanel,
                optionConfig.useViewportQueries ? styles.viewerSidePanel_viewport : styles.viewerSidePanel_container,
              )}
            >
              {!sidePanelOpen && (
                <StickySidebar topOffset={STICKY_HEADER_SIDEBAR_OFFSET} recalculateOffsetOnResize={stickyHeaderEnabled}>
                  {metadataPane}
                </StickySidebar>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <Header
            issue={issue}
            headerSecondary={secondaryIssueData}
            parent={secondaryIssueData?.parent}
            linkedSecurityAlertsData={secondaryIssueData}
            blockedBySecondaryData={secondaryIssueData}
            trackedBySecondaryData={secondaryIssueData}
            subIssueSecondaryData={secondaryIssueData}
            optionConfig={effectiveOptionConfig}
          />
          <div ref={sentinelRef} className={styles.stickyHeaderSentinel} aria-hidden="true" />
          <ContentWrapper>
            <div
              className={clsx(
                styles.contentAndSidebarWrapper,
                optionConfig.useViewportQueries
                  ? styles.contentAndSidebarWrapper_viewport
                  : styles.contentAndSidebarWrapper_container,
              )}
            >
              {issueContentArea}
              <div
                data-testid={TEST_IDS.issueViewerMetadataContainer}
                className={clsx(
                  styles.metadataSidebar,
                  optionConfig.useViewportQueries ? styles.metadataSidebar_viewport : styles.metadataSidebar_container,
                )}
              >
                {metadataPane}
              </div>
            </div>
          </ContentWrapper>
        </>
      )}
      {stickyHeaderEnabled &&
        sidePanelOpen &&
        (optionConfig.insideSidePanel ? (
          <SidePanelMetadataDrawer onClose={() => setSidePanelOpen(false)} returnFocusRef={sidePanelReturnFocusRef}>
            {metadataPane}
          </SidePanelMetadataDrawer>
        ) : (
          <Dialog
            title="Details"
            position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
            width="360px"
            returnFocusRef={sidePanelReturnFocusRef}
            onClose={() => setSidePanelOpen(false)}
          >
            {metadataPane}
          </Dialog>
        ))}
      {secondaryIssueData && createDialogOpen && activeIssueId === issue.id && (
        <Suspense fallback={null}>
          <SubIssuesCreateDialog
            open={createDialogOpen}
            setOpen={closeCreateDialog}
            issue={secondaryIssueData}
            onCreateSuccess={({createMore}): void => {
              if (!createMore) {
                closeCreateDialog()
              }
            }}
          />
        </Suspense>
      )}
    </TimelineConnectionProvider>
  )
}

function IsssueViewerWithSecondary({
  owner,
  repo,
  number,
  issueViewerViewRef,
  optionConfig,
  ...rest
}: IssueViewerWithSecondaryProps) {
  const [secondaryIssueData] = useSecondaryQuery({owner, repo, number})

  // Defensive check: ensure query ref variables match requested issue to prevent stale data during navigation
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  return (
    <IssueViewerInternal
      owner={owner}
      repo={repo}
      number={number}
      issueViewerViewRef={issueViewerViewRef}
      optionConfig={optionConfig}
      secondaryIssueData={secondaryIssueData ?? undefined}
      {...rest}
    />
  )
}
